import { useTranslation } from "react-i18next";
import FAQHeader from "../FAQHeader";
import FAQNavigation from "../FAQNavigation";
import SignUpBanner from "../SignUpBanner";
import LiiferLogoIcon from "../../../../assets/images/small_logo_with_background.png";
import hotkeyConfigPng1 from "../../../../assets/images/hotkey-configure-1.png";
import hotkeyConfigPng2 from "../../../../assets/images/hotkey-configure-2.png";
import { useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

export const AnyOtherQuestionsPage = () => {
  const { t } = useTranslation();
  const hotkeysRef = useRef<HTMLDivElement | null>(null);
  const deletionQuestionRef = useRef<HTMLDivElement | null>(null);
  const currentLocation = useLocation();

  useEffect(() => {
    if (currentLocation.hash === "#responsibilities" && hotkeysRef.current) {
      hotkeysRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [currentLocation]);

  return (
    <>
      <Helmet>
        <title>Liifer In-Game App and Account Management - Liifer FAQ</title>
        <meta
          name="description"
          content="Find out how to change the hotkey for Liifer's in-game app, delete your account, and learn about the impact on game performance. Discover the differences between the in-game app and desktop app, customization options, payment methods, and referral program."
        />
        <meta
          name="keywords"
          content="Liifer, in-game app, keybind, hotkey, delete account, game performance, desktop app, custom orders, payment methods, referrals, supported games"
        />
      </Helmet>
      <FAQHeader />
      <div className="flex flex-col md:flex-row">
        <FAQNavigation />
        <div className="flex-col lg:flex md:w-2/3">
          <div className="px-4 md:p-8 md:pt-14 lg:hidden">
            <SignUpBanner />
          </div>
          <div className="w-full md:h-screen mt-4 md:mt-8 lg:mt-16 md:overflow-auto hide-scrollbar px-10 md:pb-96 lg:pb-20">
            <div className="flex flex-col space-y-16 pb-20 md:pb-32 text-slate-200 pt-5 text-left">
              <div ref={hotkeysRef} id="hotkeys">
                <div className="flex items-center">
                  <img
                    className="h-12 pr-4"
                    src={LiiferLogoIcon}
                    alt="Liifer logo Icon"
                  />
                  <h1 className="font-semibold text-2xl">
                    {t("FAQ.howDoIChangeHotkeys")}
                  </h1>
                </div>
                <div className="space-y-2 pt-4">
                  <div>
                    <p className="space-x-1">
                      <span>{t("FAQ.howDoIChangeHotkeysAnswer1")}</span>
                      <span className="underline">
                        {t("FAQ.overwolfSettingsPage")}
                      </span>
                      <span>{t("FAQ.howDoIChangeHotkeysAnswer2")}</span>
                    </p>
                  </div>

                  <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-1 pt-4">
                    <div className="h-5/6 md:h-max md:w-1/4 mx-auto">
                      <img src={hotkeyConfigPng1} alt="step one" />
                    </div>
                    <div className="-ml-4 md:ml-0 md:scale-100 pt-4 md:w-3/4 flex justify-center items-center">
                      <img src={hotkeyConfigPng2} alt="step two" />
                    </div>
                  </div>
                </div>
              </div>
              <div ref={deletionQuestionRef} id="accountdeletion">
                <div className="flex items-center">
                  <img
                    className="h-12 pr-4"
                    src={LiiferLogoIcon}
                    alt="Liifer logo Icon"
                  />
                  <h1 className="font-semibold text-2xl">
                    {t("FAQ.accountDeletion")}
                  </h1>
                </div>
                <div className="space-y-2 pt-4">
                  <p className="space-x-1">
                    <span>{t("FAQ.accountDeletionAnswer1")}</span>
                    <a
                      draggable={false}
                      className="underline text-blue-400 hover:text-blue-300"
                      href="mailto:info@liifer.com"
                    >
                      {t("FAQ.link")}
                    </a>
                    <span>{t("FAQ.orEmailUs")}</span>

                    <span>{t("FAQ.accountDeletionAnswer2")}</span>
                  </p>
                </div>
              </div>
              <div>
                <div className="flex items-center">
                  <img
                    className="h-12 pr-4"
                    src={LiiferLogoIcon}
                    alt="Liifer logo Icon"
                  />
                  <h1 className="font-semibold text-2xl">
                    {t("FAQ.willItEffectFPS")}
                  </h1>
                </div>
                <div className="space-y-2 pt-4">
                  <p>{t("FAQ.willItEffectFPSAnswer")}</p>
                </div>
              </div>
              <div>
                <div className="flex items-center">
                  <img
                    className="h-12 pr-4"
                    src={LiiferLogoIcon}
                    alt="Liifer logo Icon"
                  />
                  <h1 className="font-semibold text-2xl">
                    {t("FAQ.differenceBetweenInGameAndDesktop")}
                  </h1>
                </div>
                <div className="space-y-2 pt-4">
                  <p>{t("FAQ.differenceBetweenInGameAndDesktopAnswerP1")}</p>
                  <p>{t("FAQ.differenceBetweenInGameAndDesktopAnswerP2")}</p>
                  <p>{t("FAQ.differenceBetweenInGameAndDesktopAnswerP3")}</p>
                  <p>{t("FAQ.differenceBetweenInGameAndDesktopAnswerP4")}</p>
                </div>
              </div>
              <div>
                <div className="flex items-center">
                  <img
                    className="h-12 pr-4"
                    src={LiiferLogoIcon}
                    alt="Liifer logo Icon"
                  />
                  <h1 className="font-semibold text-2xl">
                    {t("FAQ.possibleToCustomize")}
                  </h1>
                </div>
                <div className="space-y-2 pt-4">
                  <p>{t("FAQ.possibleToCustomizeAnswer")}</p>
                </div>
              </div>
              <div>
                <div className="flex items-center">
                  <img
                    className="h-12 pr-4"
                    src={LiiferLogoIcon}
                    alt="Liifer logo Icon"
                  />
                  <h1 className="font-semibold text-2xl">
                    {t("FAQ.whatPaymentMethod")}
                  </h1>
                </div>
                <div className="space-y-2 pt-4">
                  <p>{t("FAQ.whatPaymentMethodAnswer")}</p>
                </div>
              </div>
              <div>
                <div className="flex items-center">
                  <img
                    className="h-12 pr-4"
                    src={LiiferLogoIcon}
                    alt="Liifer logo Icon"
                  />
                  <h1 className="font-semibold text-2xl">
                    {t("FAQ.howToJoin")}
                  </h1>
                </div>
                <div className="space-y-2 pt-4">
                  <p>{t("FAQ.howToJoinAnswer")}</p>
                  <a
                    className="text-highlight-teal"
                    href="https://www.liifer.com/signup"
                    draggable={false}
                  >
                    https://www.liifer.com/signup
                  </a>
                </div>
              </div>

              <div>
                <div className="flex items-center">
                  <img
                    className="h-12 pr-4"
                    src={LiiferLogoIcon}
                    alt="Liifer logo Icon"
                  />
                  <h1 className="font-semibold text-2xl">
                    {t("FAQ.canIReferFriends")}
                  </h1>
                </div>
                <div className="space-y-2 pt-4">
                  <div className="space-y-2">
                    <p>{t("FAQ.canIReferFriendsAnswerP1")}</p>
                    <p>{t("FAQ.canIReferFriendsAnswerP2")}</p>
                    <p>{t("FAQ.canIReferFriendsAnswerP3")}</p>
                    <p>{t("FAQ.canIReferFriendsAnswerP4")}</p>
                  </div>
                  <div className="flex flex-col pt-4">
                    <small>{t("FAQ.canIReferFriendsList.one")}</small>
                    <small>{t("FAQ.canIReferFriendsList.three")}</small>
                    <small>{t("FAQ.canIReferFriendsList.four")}</small>
                    <small>{t("FAQ.canIReferFriendsList.five")}</small>
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center">
                  <img
                    className="h-12 pr-4"
                    src={LiiferLogoIcon}
                    alt="Liifer logo Icon"
                  />
                  <h1 className="font-semibold text-2xl">
                    {t("FAQ.whatGames")}
                  </h1>
                </div>
                <div className="space-y-2 pt-4">
                  <p>{t("FAQ.whatGamesAnswer")}</p>
                  <a
                    target="_blank"
                    rel="noreferrer"
                    className="underline text-blue-400 hover:text-blue-300"
                    href="https://www.overwolf.com/supported-games/"
                  >
                    https://www.overwolf.com/supported-games/
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="p-8 pt-12 hidden lg:block">
          <SignUpBanner />
        </div>
      </div>
    </>
  );
};

export default AnyOtherQuestionsPage;
